/** Student card for the management {@link DataTable}. */
import * as React from 'react';
import { User, GraduationCap, Baby, BookOpen, Heart } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import type { Student } from '@/types/inperson/student.types';
import {
  deriveStudentDisplay,
  formatStudentSubjectCountLabel,
  getStudentDisplayName,
  getStudentSubjectCount,
  type StudentPageActions,
} from './student-table-utils';

/** Props for {@link StudentCard}. */
interface StudentCardProps {
  student: Student;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  pageActions?: StudentPageActions;
}

/** Student card for the management {@link DataTable}. */
export const StudentCard: React.FC<StudentCardProps> = ({
  student,
  onView,
  onEdit,
  onDelete,
  pageActions,
}) => {
  const canEdit = pageActions?.canEditRow ?? true;
  const canDelete = pageActions?.canDeleteRow ?? true;
  const { statusConfig, StatusIcon, profile, levelLabel, isMinor, hasSpecialNeeds, genderConfig } =
    deriveStudentDisplay(student);

  const subjectCountLabel = formatStudentSubjectCountLabel(getStudentSubjectCount(profile));

  function renderGenderBadge(): React.ReactNode {
    if (!genderConfig) return null;
    return (
      <LabelBadge
        label={genderConfig.label}
        icon={genderConfig.icon}
        variant="outlined"
        color={genderConfig.color}
        size="sm"
      />
    );
  }

  const handleClick = () => {
    onView?.(student.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onView?.(student.id);
    }
  };

  return (
    <TableCard
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      ariaLabel={`Voir les détails de ${student.displayName || 'cet élève'}`}
    >
      <div className="data-card-content">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[color-mix(in_srgb,var(--color-1-nuance-1)_12%,transparent)]">
            <User className="w-5 h-5 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="font-semibold text-foreground text-base truncate">
              {getStudentDisplayName(student)}
            </span>
            <div className="flex items-center flex-wrap gap-2 mt-0.5 h-[52px] content-start">
              <LabelBadge
                label={statusConfig.label}
                icon={StatusIcon}
                variant={statusConfig.badgeVariant}
                color={statusConfig.badgeColor}
                size="sm"
              />
              {renderGenderBadge()}
              {isMinor && (
                <LabelBadge
                  label="Mineur"
                  icon={Baby}
                  variant="ghost"
                  color="var(--color-1-nuance-1)"
                  size="sm"
                />
              )}
              {hasSpecialNeeds && (
                <LabelBadge
                  label="Besoins spécifiques"
                  icon={Heart}
                  variant="ghost"
                  color="var(--color-3-nuance-1)"
                  size="sm"
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex-1"></div>

        <div className="flex items-center gap-2">
          <LabelBadge
            label={levelLabel ?? 'N/R'}
            icon={GraduationCap}
            variant="ghost"
            color="var(--color-2-nuance-1)"
            size="sm"
          />
          <LabelBadge
            label={subjectCountLabel}
            icon={BookOpen}
            variant="outlined"
            color="var(--color-3-nuance-1)"
            size="sm"
          />
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(student.id);
            }}
          />
          {canEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(student.id);
              }}
            />
          )}
          {canDelete && (
            <Button
              preset="row-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(student.id);
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
